/**
 * Validacion del formulario de crear oferta.
 *
 * Fuente unica de verdad: la consumen el estado deshabilitado del boton, los
 * mensajes inline de cada campo y el aviso de "falta completar". Es una funcion
 * pura para que no haya forma de que una de esas tres cosas se desincronice de
 * las otras.
 */

import { addDays, differenceInDays, startOfDay } from 'date-fns';

import { parseYmd } from '#root/utils';
import { OFFER_EXPIRATION_MAX_DAYS } from '#root/constants/business';

/** `oferta.lugent` es varchar(500) en la base de datos. */
export const DELIVERY_PLACE_MAX = 500;

/**
 * La entrega no puede ser hoy. El viajero compra recien despues de que le pagan,
 * el producto viaja a su domicilio en EE.UU. y luego el vuela: nada de eso cabe
 * en el mismo dia (ver el flujo de BUSINESS_MODEL.md).
 */
export const MIN_DELIVERY_DAYS = 1;

/** La oferta debe vencer ANTES de la entrega, no el mismo dia. */
export const EXPIRATION_MARGIN_DAYS = 1;

export type OfferField =
  | 'quantity'
  | 'real_price'
  | 'traveler_fee'
  | 'delivery_places'
  | 'delivery_date'
  | 'expiration_date';

export type OfferFormErrors = Partial<Record<OfferField, string>>;

interface ValidateContext {
  /** Cantidad maxima ofertable: `order.can`. */
  maxQuantity: number;
}

/**
 * Etiquetas para el aviso bajo el boton. Los tres campos que viven dentro del
 * acordeon comparten etiqueta a proposito: al viajero le sirve saber que seccion
 * abrir, no el nombre exacto del campo.
 */
const FIELD_LABEL: Record<OfferField, string> = {
  quantity: 'Cantidad',
  real_price: 'Precio real',
  traveler_fee: 'Tu comisión',
  delivery_places: 'Datos de entrega',
  delivery_date: 'Datos de entrega',
  expiration_date: 'Datos de entrega',
};

/** Campos que llegan prellenados: si estan invalidos, se avisa de inmediato. */
const PREFILLED_FIELDS: OfferField[] = [
  'quantity',
  'delivery_places',
  'delivery_date',
  'expiration_date',
];

export const isPrefilledField = (field: OfferField) => PREFILLED_FIELDS.includes(field);

/** El picker devuelve `null` al vaciarlo con el teclado. */
const isEmptyDate = (value: unknown): boolean =>
  value === null || value === undefined || value === '';

const toDate = (value: unknown): Date =>
  value instanceof Date ? value : new Date(value as any);

/**
 * Un `Date` invalido sigue siendo un objeto, asi que `!fecha` no lo detecta.
 * Los pickers de MUI aceptan texto libre y con un formato a medias devuelven
 * `new Date(NaN)`.
 */
const isValidDate = (value: unknown): boolean => {
  if (isEmptyDate(value)) return false;
  return !Number.isNaN(toDate(value).getTime());
};

/** Precio y comision llegan como string desde el input. */
const parseAmount = (value: unknown): number | null => {
  const raw = String(value ?? '').trim();
  if (raw === '') return null;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? null : parsed;
};

export const validateOffer = (offer: any, ctx: ValidateContext): OfferFormErrors => {
  const errors: OfferFormErrors = {};
  const { maxQuantity } = ctx;

  // Cantidad
  const quantity = parseAmount(offer?.quantity);
  if (quantity === null) {
    errors.quantity = `Número entre 1 y ${maxQuantity}`;
  } else if (quantity < 1) {
    errors.quantity = 'Mínimo 1';
  } else if (quantity > maxQuantity) {
    errors.quantity = `Máximo ${maxQuantity}`;
  }

  // Precio real: `!"0"` es false, asi que hay que comparar contra 0 de verdad.
  const realPrice = parseAmount(offer?.real_price);
  if (realPrice === null) {
    errors.real_price = 'Requerido';
  } else if (realPrice <= 0) {
    errors.real_price = 'Debe ser mayor a 0';
  }

  // Comision del viajero
  const travelerFee = parseAmount(offer?.traveler_fee);
  if (travelerFee === null) {
    errors.traveler_fee = 'Requerido';
  } else if (travelerFee <= 0) {
    errors.traveler_fee = 'Debe ser mayor a 0';
  }

  // Lugar de entrega: con trim, para que solo espacios cuente como vacio.
  const deliveryPlace = String(offer?.delivery_places ?? '').trim();
  if (!deliveryPlace) {
    errors.delivery_places = 'Indica dónde entregarás el pedido';
  } else if (deliveryPlace.length > DELIVERY_PLACE_MAX) {
    errors.delivery_places = `Máximo ${DELIVERY_PLACE_MAX} caracteres`;
  }

  // Fecha de entrega. El `minDate` del picker ya impide elegir una fecha pasada
  // en el calendario, pero se puede escribir a mano: sin esta regla el control se
  // pinta de rojo y el boton sigue habilitado.
  const deliveryDate = offer?.delivery_date;
  if (isEmptyDate(deliveryDate)) {
    errors.delivery_date = 'Indica la fecha de entrega';
  } else if (!isValidDate(deliveryDate)) {
    errors.delivery_date = 'Fecha inválida';
  } else if (
    startOfDay(toDate(deliveryDate)) < addDays(startOfDay(new Date()), MIN_DELIVERY_DAYS)
  ) {
    errors.delivery_date = 'Debe ser al menos mañana';
  }

  // Vigencia: la resuelve un <Select> de opciones fijas, nunca deberia fallar.
  if (!isValidDate(offer?.expiration_date)) {
    errors.expiration_date = 'Fecha de vigencia inválida';
  }

  return errors;
};

/** Etiquetas de lo que falta, sin repetir la seccion de entrega. */
export const getMissingLabels = (errors: OfferFormErrors): string[] => {
  const labels = (Object.keys(errors) as OfferField[]).map((field) => FIELD_LABEL[field]);
  return Array.from(new Set(labels));
};

/** Primer mensaje de error, para el toast de ultimo recurso. */
export const getFirstErrorMessage = (errors: OfferFormErrors): string | null => {
  const first = (Object.keys(errors) as OfferField[])[0];
  return first ? errors[first] ?? null : null;
};

/* ------------------------------------------------------------- vigencia --- */

interface ExpirationLimits {
  /** `trip.arrive_date`, string "Y-m-d" del viaje. */
  arriveDate?: string | number | null;
  /** `offer.delivery_date`: la de la OFERTA, editable, no la del viaje. */
  deliveryDate?: unknown;
}

/**
 * Tope de vigencia en dias desde hoy: el menor entre el tope de producto, la
 * fecha de viaje y la fecha de entrega de la oferta.
 *
 * Espeja `Trip::clamp_offer_expiration()` del backend, que recorta por los mismos
 * tres valores. Sin esto la UI ofrece vigencias que el server luego recorta en
 * silencio, y el viajero cree haber ofrecido mas dias de los que se guardaron.
 */
export const getMaxExpirationDays = ({ arriveDate, deliveryDate }: ExpirationLimits) => {
  const today = startOfDay(new Date());
  const limits = [OFFER_EXPIRATION_MAX_DAYS];

  const arrive = parseYmd(arriveDate as any);
  if (arrive) {
    limits.push(differenceInDays(startOfDay(arrive), today));
  }
  if (isValidDate(deliveryDate)) {
    // Menos el margen: la oferta vence antes de la entrega, no el mismo dia.
    limits.push(
      differenceInDays(startOfDay(toDate(deliveryDate)), today) - EXPIRATION_MARGIN_DAYS,
    );
  }

  // "Hoy" siempre queda como piso, aunque las fechas vengan vencidas.
  return Math.max(0, Math.min(...limits));
};

/** Recorta una vigencia al tope. Devuelve la misma fecha si ya cabia. */
export const clampExpirationDate = (expiration: unknown, limits: ExpirationLimits): Date => {
  const max = addDays(startOfDay(new Date()), getMaxExpirationDays(limits));
  if (!isValidDate(expiration)) return max;
  const current = toDate(expiration);
  return startOfDay(current) > max ? max : current;
};
